import * as Sentry from '@sentry/react';
import pack from '../../package.json';

const { VITE_SENTRY_DSN, VITE_SENTRY_SAMPLE_RATE_PERCENT } = import.meta.env;

const config = {
  dsn: VITE_SENTRY_DSN,
  // Version from package.json
  release: pack.version,
};

const STALE_CHUNK_MESSAGE_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /failed to load module script/i,
  /importing a module script failed/i,
  // Safari: undefined is not an object (evaluating 'e._result.default')
  /_result\.default/i,
  // Chrome: same React.lazy read, without `_result` in the message
  /cannot read properties of undefined \(reading 'default'\)/i,
  // Firefox: can't access property "default", e._result is undefined
  /_result is undefined/i,
];

export function isStaleChunkLoadMessage(message: string): boolean {
  return STALE_CHUNK_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

export const initSentry = (): typeof Sentry | null => {
  if (!config.dsn) {
    return null;
  }
  Sentry.init({
    dsn: config.dsn,
    release: config.release,
    integrations: [Sentry.browserTracingIntegration()],

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    tracesSampleRate: parseFloat(VITE_SENTRY_SAMPLE_RATE_PERCENT || '10') / 100,

    // Filter out specific backend errors that are not actionable from frontend
    beforeSend(event, _hint) {
      const eventMessage = [
        event.message,
        ...(event.exception?.values?.map((value) => value.value) ?? []),
      ].filter(Boolean).join(' ');

      // Drop events with "Object Not Found Matching Id" for simulateEvent and update methods
      // This filters out backend API errors that bubble up as unhandled promise rejections
      if (/Object Not Found Matching Id:.*MethodName:(simulateEvent|update)/i.test(eventMessage)) {
        return null;
      }

      // Drop stale-chunk import failures; chunkRecovery reloads the last good route
      if (isStaleChunkLoadMessage(eventMessage)) {
        return null;
      }

      // Drop provider disconnection errors - these are likely from browser extensions
      // or third-party scripts, not from the application itself
      if (/The provider is disconnected from all chains/i.test(eventMessage)) {
        return null;
      }

      return event;
    },
  });

  return Sentry;
};
